import { postInteraction } from './autointel.service';
import { InteractionAction, ConfigContext } from './autointel.types';

// Event types for AutoIntel interactions
export const AutoIntelEvents = {
  // Selection events
  EXTERIOR_COLOR_SELECT: 'exterior-color-select',
  INTERIOR_COLOR_SELECT: 'interior-color-select',
  VARIANT_SELECT: 'variant-select',
  ACCESSORY_SELECT: 'accessory-select',
  FEATURE_SELECT: 'feature-select',
  WHEEL_SELECT: 'wheel-select',
  PACKAGE_SELECT: 'package-select',

  // View events
  VIEW_CHANGE_INTERIOR: 'view-change-interior',
  VIEW_CHANGE_EXTERIOR: 'view-change-exterior',
  INTERACTION_DOORS_OPEN: 'interaction-doors-open',
  INTERACTION_BOOT_OPEN: 'interaction-boot-open',
  INTERACTION_SUNROOF: 'interaction-sunroof',
  INTERACTION_HEADLIGHT: 'interaction-headlight',
  INTERACTION_TAILIGHT: 'interaction-tailight',
  HIGHLIGHT_VIEW: 'highlight-view',
  CAMERA_VIEW_CHANGE: 'camera-view-change',

  // Conversion events
  CONVERSION_EMAIL: 'conversion-email',
  CONVERSION_TEST_DRIVE: 'conversion-test-drive',
  CONVERSION_PHONE: 'conversion-phone',
  CONVERSION_GET_QUOTE: 'conversion-get-quote',
  CONVERSION_SAVE: 'conversion-save',
} as const;

export type AutoIntelEventType =
  (typeof AutoIntelEvents)[keyof typeof AutoIntelEvents];

// Map our events to AutoIntel action types
const eventToAction: Record<string, InteractionAction> = {
  [AutoIntelEvents.EXTERIOR_COLOR_SELECT]: 'SELECT',
  [AutoIntelEvents.INTERIOR_COLOR_SELECT]: 'SELECT',
  [AutoIntelEvents.VARIANT_SELECT]: 'SELECT',
  [AutoIntelEvents.ACCESSORY_SELECT]: 'SELECT',
  [AutoIntelEvents.FEATURE_SELECT]: 'SELECT',
  [AutoIntelEvents.WHEEL_SELECT]: 'SELECT',
  [AutoIntelEvents.PACKAGE_SELECT]: 'SELECT',
  [AutoIntelEvents.VIEW_CHANGE_INTERIOR]: 'VIEW',
  [AutoIntelEvents.VIEW_CHANGE_EXTERIOR]: 'VIEW',
  [AutoIntelEvents.INTERACTION_DOORS_OPEN]: 'VIEW',
  [AutoIntelEvents.INTERACTION_BOOT_OPEN]: 'VIEW',
  [AutoIntelEvents.INTERACTION_SUNROOF]: 'VIEW',
  [AutoIntelEvents.INTERACTION_HEADLIGHT]: 'VIEW',
  [AutoIntelEvents.INTERACTION_TAILIGHT]: 'VIEW',
  [AutoIntelEvents.HIGHLIGHT_VIEW]: 'VIEW',
  [AutoIntelEvents.CAMERA_VIEW_CHANGE]: 'VIEW',
  [AutoIntelEvents.CONVERSION_EMAIL]: 'CONVERT',
  [AutoIntelEvents.CONVERSION_TEST_DRIVE]: 'CONVERT',
  [AutoIntelEvents.CONVERSION_PHONE]: 'CONVERT',
  [AutoIntelEvents.CONVERSION_GET_QUOTE]: 'CONVERT',
  [AutoIntelEvents.CONVERSION_SAVE]: 'CONVERT',
};

// Map events to subAction descriptions
const eventToSubAction: Record<string, string> = {
  [AutoIntelEvents.EXTERIOR_COLOR_SELECT]: 'Exterior Selection',
  [AutoIntelEvents.INTERIOR_COLOR_SELECT]: 'Interior Selection',
  [AutoIntelEvents.VARIANT_SELECT]: 'Variant Selection',
  [AutoIntelEvents.ACCESSORY_SELECT]: 'Accessory Selection',
  [AutoIntelEvents.FEATURE_SELECT]: 'Feature Selection',
  [AutoIntelEvents.WHEEL_SELECT]: 'Wheel Selection',
  [AutoIntelEvents.PACKAGE_SELECT]: 'Package Selection',
  [AutoIntelEvents.VIEW_CHANGE_INTERIOR]: 'View Change - Interior',
  [AutoIntelEvents.VIEW_CHANGE_EXTERIOR]: 'View Change - Exterior',
  [AutoIntelEvents.INTERACTION_DOORS_OPEN]: 'Interactions Change - Doors',
  [AutoIntelEvents.INTERACTION_BOOT_OPEN]: 'Interactions Change - Boot',
  [AutoIntelEvents.INTERACTION_SUNROOF]: 'Interactions Change - Sunroof',
  [AutoIntelEvents.INTERACTION_HEADLIGHT]: 'Interactions Change - Headlight',
  [AutoIntelEvents.INTERACTION_TAILIGHT]: 'Interactions Change - Tailight',
  [AutoIntelEvents.HIGHLIGHT_VIEW]: 'Highlights',
  [AutoIntelEvents.CAMERA_VIEW_CHANGE]: 'Camera View Change',
  [AutoIntelEvents.CONVERSION_EMAIL]: 'E-mail Subscription',
  [AutoIntelEvents.CONVERSION_TEST_DRIVE]: 'Test Drive Request',
  [AutoIntelEvents.CONVERSION_PHONE]: 'Callback Request',
  [AutoIntelEvents.CONVERSION_GET_QUOTE]: 'Get Quote Request',
  [AutoIntelEvents.CONVERSION_SAVE]: 'Save Configuration',
};

/**
 * Dispatch an AutoIntel interaction event
 * Can be called from anywhere in the app
 *
 * @param eventType - The type of event from AutoIntelEvents
 * @param itemId - The item identifier (e.g., color code, feature id)
 * @param additionalSubAction - Optional additional context for the subAction
 */
export const dispatchAutoIntelEvent = (
  eventType: AutoIntelEventType | string,
  itemId: string,
  additionalSubAction?: string,
  configContext?: Partial<ConfigContext>
): void => {
  const action = eventToAction[eventType];
  const baseSubAction = eventToSubAction[eventType];

  if (!action || !baseSubAction) {
    console.warn(`[AutoIntel] Unknown event type: ${eventType}`);
    return;
  }

  // Use space for interactions to match "Interactions Change - Doors Open"
  // Use dash for others to match "Highlights - [Name]"
  const separator = eventType.startsWith('interaction') ? ' ' : ' - ';

  const subAction = additionalSubAction
    ? `${baseSubAction}${separator}${additionalSubAction}`
    : baseSubAction;

  console.log(
    `[AutoIntel] Interaction: action=${action}, subAction="${subAction}", itemId=${itemId}`
  );

  // Fire and forget - don't block UI
  postInteraction(itemId, action, subAction, configContext).catch((error) => {
    console.error('[AutoIntel] Failed to post interaction:', error);
  });
};

/**
 * Helper to create a prefixed item ID for interactions
 * Useful for consistent naming conventions
 *
 * @param prefix - The prefix (e.g., 'paint', 'fabric', 'feature')
 * @param id - The item ID
 * @returns Formatted item ID string
 */
export const createItemId = (prefix: string, id: string): string => {
  return `${prefix}-${id.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
};
